/**
 * Representation of video data, user's stats for that video, and player state.
 */
import gql from "graphql-tag";
import icepick from "icepick";

import {GoogleAnalytics} from "@repo/analytics-google";
// TODO(jvoll): OK to import BigBingoInternals from here but ideally we
// would not need to. We dependency-inject the markConversion function from
// a ConversionContext when possible but as a fallback (i.e. if markConversion
// is not set on the Backbone model) we use this directly imported version.
// I'm unsure how often thisoccurs or if this is necessary but figuring that
// out is out of scope right now.
import {
    enums,
    type Conversion,
    type MarkConversionT,
} from "@repo/analytics-learning-time";
// eslint-disable-next-line no-restricted-imports
import * as BigBingoInternals from "@repo/analytics-learning-time/bigbingo-internals";
import * as Cookies from "@repo/data-cookies";
import type {ApolloResult} from "@repo/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import Log, {Errors} from "@repo/logging";
import {PermissionsInfo, RequestInfo} from "@repo/shared-core";

// TODO(somewhatabstract, JIRA-XXX3-20210907): Improve our patterns so that we
// can pass a valid ApolloGqlClient from the react components.
// DO NOT COPY - bad example of how to get a ApolloGqlClient.
// eslint-disable-next-line no-restricted-imports
import {getUnsafeApolloGqlClient} from "#data-access/gql/get-unsafe-apollo-gql-client.ts";
import {emitVideoAlmostComplete} from "#events/video-almost-complete-event.ts";
import APIActionResults from "#shared-package/api-action-results.ts";
import Backbone, {type ModelOpts} from "#shared-package/backbone-lite.js";
import type {
    GraphQLError,
    VideoGraphQLActionResults,
} from "#shared-package/types.ts";
import type {Action} from "#user-info-store-package/user-info-reducer.ts";
import userInfoStore from "#user-info-store-package/user-info-store.ts";

import {VideoPlayerState} from "./constants.ts";
import {getYoutubeLanguage} from "./youtube-language.ts";

type VideoPlayerStateT =
    (typeof VideoPlayerState)[keyof typeof VideoPlayerState];

type VideoLogState = {
    secondsWatchedSinceSave: number;
    percentLastSaved: number;
    lastPollTime: Date;
    lastSecondWatched?: number;
};

type EventCode = // No event-code: we're going to log *right now*!

        | "" // executed "falling asleep" codepath
        | "a" // no need to flush.
        | "b" // attempted to save while already saving.
        | "c" // cookies are disabled.
        | "d"
        | "e"; // too many failures

type VideoItemProgress = {
    content: {
        id: string;
        progressKey: string;
        // JSONString
        downloadUrls: string;
    };
    lastSecondWatched: number;
    secondsWatched: number;
    lastWatched: string;
    points: number;
    started: boolean;
    completed: boolean;
};

type UpdateUserVideoProgressData = {
    updateUserVideoProgress: {
        error: GraphQLError | null | undefined;
        videoItemProgress: VideoItemProgress | null | undefined;
        actionResults: VideoGraphQLActionResults;
    };
};

type UpdateUserVideoProgressVariables = {
    input: {
        contentId: string;
        secondsWatched: number;
        lastSecondWatched: number;
        durationSeconds?: number | null | undefined;
        lessonId?: string;
        captionsLocale?: string | null | undefined;
        fallbackPlayer?: boolean | null | undefined;
        localTimezoneOffsetSeconds?: number | null | undefined;
        gaReferrer?: string | null | undefined;
    };
};

// Strip keys with `undefined` values from an object.
const stripUndefined = <T extends Record<any, any>>(obj: T): T =>
    Object.entries(obj)
        .filter(([_, value]: [string, any]) => value !== undefined)
        .reduce(
            (acc, [key, value]: [string, any]) => ({...acc, [key]: value}),
            {} as any,
        );

export const UPDATE_USER_VIDEO_PROGRESS_MUTATION: GqlOperation<
    UpdateUserVideoProgressData,
    UpdateUserVideoProgressVariables
> = gqlOp(gql`
    mutation updateUserVideoProgress($input: UserVideoProgressInput!) {
        updateUserVideoProgress(videoProgressUpdate: $input) {
            videoItemProgress {
                content {
                    id
                    progressKey
                    ... on Video {
                        downloadUrls
                    }
                }
                lastSecondWatched
                secondsWatched
                lastWatched
                points
                started
                completed
            }
            actionResults {
                pointsEarned {
                    points
                }
                tutorialNodeProgress {
                    contentId
                    progress
                }
                userProfile {
                    countVideosCompleted
                    points
                    countBrandNewNotifications
                }
                notificationsAdded {
                    badges
                    avatarParts
                    readable
                    urgent
                    toast
                    continueUrl
                }
                ... on VideoActionResults {
                    currentTask {
                        id
                        content {
                            id
                        }
                        pointBounty
                    }
                }
            }
            error {
                code
                debugMessage
            }
        }
    }
`);

type VideoPlayerModelType = TSFixMe<"Create a real type for this">;
/**
 * Representation of video data, user's stats for that video, and player state.
 */
const VideoPlayerModel: VideoPlayerModelType = Backbone.Model.extend({
    MIN_SECONDS_BETWEEN_SAVES: 30,
    MIN_PERCENT_BETWEEN_SAVES: 0.1,
    MIN_SECONDS_BETWEEN_SAVES_PHANTOM: 60,
    MIN_PERCENT_BETWEEN_SAVES_PHANTOM: 0.25,

    // Autosave and check we're awake every 10s
    SANITY_CHECK_POLL_INTERVAL: 10000,

    REQUIRED_PERCENT_FOR_FULL_POINTS: 0.9,
    VIDEO_ALMOST_COMPLETE_PERCENT: 0.9,

    defaults: {
        points: 0,
        pointsSaved: 0,
        possiblePoints: 750,
        videoKey: "",
        contentId: "",
        youtubeId: "0",
        liteVideo: false,
        playbackSpeedAvailable: false,
        playbackRate: 1.0,
        playerState: VideoPlayerState.UNSTARTED,
        numPolls: 0, // incremented every time we call _handleVideoLogPoll
        playerStateHistory: "", // updated every time we call _handleVideo...
        showOptions: true,
        showPoints: true,
        showInfo: true,
        showShare: true,
        showEditCaptions: false,
        transcriptAvailable: false,
        topicID: undefined,
        lessonID: undefined,
        useCaptionsDetectionFallback: true, // While true, use fallback.
        captionsLocale: "", // This means captions were off.
        hasPlayed: false,
        onComplete: undefined,
        onStart: undefined,
        getCurrentTimeIsDefinitelyWorking: false,
        userHasHitPauseOnThisVideo: false,
        _videoLogState: {
            secondsWatchedSinceSave: 0,
            percentLastSaved: 0.0,
            lastPollTime: new Date(),
        },
    },

    player: null,

    initialize: function (this: VideoPlayerModelType, options: ModelOpts) {
        // TODO(riley): Written to match `_.bindAll`, but do we really need to
        //              re-bind things up the prototype chain? Do we need to
        //              re-bind them at all?
        const ownMethods: Array<string> = [];
        for (const property in this) {
            if (typeof this[property] === "function") {
                ownMethods.push(property);
            }
        }
        ownMethods.forEach(
            (method) => (this[method] = this[method].bind(this)),
        );

        if (PermissionsInfo.isLoggedIn) {
            this.set({
                secondsBetweenSaves: this.MIN_SECONDS_BETWEEN_SAVES,
                percentBetweenSaves: this.MIN_PERCENT_BETWEEN_SAVES,
                maxConsecutiveFailures: 100,
            });
        } else {
            this.set({
                secondsBetweenSaves: this.MIN_SECONDS_BETWEEN_SAVES_PHANTOM,
                percentBetweenSaves: this.MIN_PERCENT_BETWEEN_SAVES_PHANTOM,
                maxConsecutiveFailures: 3,
            });
        }

        this.maybeShowEditCaptionsButton(
            options.slug,
            options.translatedYoutubeLang,
        );

        this.consecutiveFailures = 0;
        this.listenToVideoStart();

        // The thumbnailUrls dictionary isn't present on unpublished content
        // because they haven't had any thumbnails generated.
        // (It's actually not shown on any revision entities.)
        // In this case, we use an empty URL for the poster frame image,
        // which will cause the CSS to just be transparent
        // and, essentially, fall back to the YouTube thumbnail.
        const thumbnailUrl = (this.get("thumbnailUrls") || {}).filtered || "";
        this.set("thumbnailUrl", thumbnailUrl);
    },

    /**
     * Binds a function that sends a Video-Start event to Google Analytics when
     * the video player's state first changes to playing.
     */
    listenToVideoStart: function (this: VideoPlayerModelType) {
        const markVideoStartOnGA = (model: any, state: number) => {
            if (state === VideoPlayerState.PLAYING) {
                GoogleAnalytics.sendEventToGA("videoStart", {
                    videoSlug: this.get("nodeSlug"),
                });
                // After the first time the video player's state changes to
                // playing, we unbind this function from observing changes in
                // video player state, which would prevent multiple Video-Start
                // GA events from being recorded for the same pageload.
                this.off("change:playerState", markVideoStartOnGA);
            }
        };
        this.on("change:playerState", markVideoStartOnGA);
    },

    /**
     * Shows the "edit captions" button if the request locale is not "en" and
     * we have a slug string.  Hides the button otherwise.
     * @param {String} slug
     * @param {String} videoLocale - a ka_locale string
     */
    maybeShowEditCaptionsButton: function (
        this: VideoPlayerModelType,
        slug: string,
        videoLocale: string,
    ) {
        const requestLocale = RequestInfo.kaLocale;

        if (!requestLocale.startsWith("en") && slug) {
            this.set({
                showEditCaptions: true,
            });
        } else {
            this.set("showEditCaptions", false);
        }
    },

    /**
     * Marks a conversion through BigBingo, using a dependency-injected function
     * if one is provided.
     */
    markConversion: function (
        this: VideoPlayerModelType,
        conversion: Conversion,
    ) {
        const markWithContext: MarkConversionT = this.get("markConversion");
        if (markWithContext) {
            markWithContext(conversion);
        } else {
            BigBingoInternals.markConversion(conversion.id, conversion.extra, {
                activity: enums.Activity.WATCHING,
            });
        }
    },

    /*
     * This function updates the number of seconds watched since the last video
     * log "save." It is called by _handleVideoLogPoll when updating
     * _videoLogState.
     */
    _getUpdatedSecondsWatched: function (
        this: VideoPlayerModelType,
        existingPlayerState: VideoPlayerStateT,
        newPlayerState: VideoPlayerStateT,
        videoLogState: VideoLogState,
        now: Date,
    ) {
        let {secondsWatchedSinceSave, lastSecondWatched} = videoLogState;

        if (
            newPlayerState === VideoPlayerState.PLAYING &&
            lastSecondWatched === undefined
        ) {
            // If the player just started playing for the first time on this
            // pageload, we need to initialize lastSecondWatched
            lastSecondWatched = this.getVideoPosition(0);
        } else if (existingPlayerState === VideoPlayerState.PLAYING) {
            // If the player has been playing, we need to update the amount of
            // time that has passed since we last saved video progress.
            // @ts-expect-error - TS2362 - The left-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type. | TS2363 - The right-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type.
            const timeElapsed = (now - videoLogState.lastPollTime) / 1000;
            const estimatedVideoPositionDelta =
                timeElapsed * this.get("playbackRate");

            // Update secondsWatchedSinceSave
            if (!secondsWatchedSinceSave) {
                secondsWatchedSinceSave = estimatedVideoPositionDelta;
            } else {
                secondsWatchedSinceSave += estimatedVideoPositionDelta;
            }
            // We need to update lastSecondWatched by the amount of video that
            // has been watched since the last poll. (Simply setting it to
            // this.getVideoPosition() would be more convenient, but this
            // causes problems with seeks; we get notified of a seek *after* it
            // happens, so we don't know which second the user was watching
            // *before* the seek.)
            lastSecondWatched = lastSecondWatched ?? 0;
            lastSecondWatched += estimatedVideoPositionDelta;
        }

        return {secondsWatchedSinceSave, lastSecondWatched};
    },

    /**
     * This function constructs the playerState debugging string.
     * This string holds the state of the video player every time
     * we poll (that is, every time _handleVideoLogPoll is called).
     * We append to this string the state of the world at poll-time,
     * and when the poll results in a log_compatability API call, we
     * emit the string as an API parameter and reset it to "".
     *
     * The playerState debugging string looks like this (optional portions
     * denoted with [<optional portion>]):
     *   <counter>[<event code>]_
     *   <old player state>[-<new player state>]_
     *   <speed>_
     *   [<YouTube API-reported player state>]_
     *   [<secondsWatchedSinceSave>_<lastSecondWatched>];
     *   <repeat...>
     *
     * The counter is incremented every time _handleVideoLogPoll is called.
     *
     * The event code is optional, but if included it indicates a control flow
     * through _handleVideoLogPoll that *didn't* lead to log_compat being
     * called. (Grep in this file for "Event-code" to decipher event code
     * values.)
     *
     * The old player state and new player state are just values of the
     * video-constants.js VideoPlayerState enums.  If _handleVideoLogPoll was
     * called because of a player-state change, both values will be printed,
     * otherwise only old_player_state will be. (Note that values might include
     * a "-", since "-1" is a valid VideoPlayerState!)
     *
     * The speed is the current playback speed of the player.
     *
     * The YouTube-API reported player state, if available, is the state that
     * the YouTube API currently believes the player is in. This is a
     * VideoPlayerState enum value.
     *
     * The secondsWatchedSinceSave and lastSecondWatched values are the
     * parameters that were (or would have been) sent to log_compat.
     */
    _updatePlayerStateHistory: function (
        this: VideoPlayerModelType,
        eventCode: EventCode,
        existingPlayerState: VideoPlayerStateT,
        newPlayerState: VideoPlayerStateT,
        secondsWatchedSinceSave: number,
        lastSecondWatched: number,
    ) {
        let playerStateHistory = this.get("playerStateHistory");
        playerStateHistory += this.get("numPolls") + eventCode;
        if (newPlayerState !== undefined) {
            playerStateHistory += `_${existingPlayerState}-${newPlayerState}`;
        } else {
            playerStateHistory += `_${existingPlayerState}`;
        }
        playerStateHistory += `_${this.get("playbackRate")}`;
        if (this.player && this.player.getPlayerState) {
            playerStateHistory += `_${this.player.getPlayerState()}`;
        }
        if (
            secondsWatchedSinceSave !== undefined ||
            lastSecondWatched !== undefined
        ) {
            playerStateHistory += `_${secondsWatchedSinceSave || "0"}`;
            playerStateHistory += `_${lastSecondWatched || "0"}`;
        }
        playerStateHistory += ";";

        this.set("playerStateHistory", playerStateHistory);
        return playerStateHistory;
    },

    /**
     * This function reports the progress of a user in watching a video; it
     * reports the number of seconds watched and the number of points earned,
     * among other things in VideoLog.
     *
     * It should be called every SANITY_CHECK_POLL_INTERVAL, as well as any
     * time the state of the video player changes (e.g. the video starts,
     * stops, buffers, or the playback rate changes). If playback stops, or if
     * the duration of watched time exceeds some autosave interval, the
     * function will report a chunk of watched time to the server.
     *
     * This function uses the Backbone attribute _videoLogState to track the
     * last time it saved progress and the number of seconds since the last
     * save. _videoLogState should not be modified anywhere else, with the
     * exception that it is set in setInitialPlayerState if the video player
     * was server-side-rendered and this javascript file was executed after
     * playback started.
     *
     * @param {number} existingState The current state of the video player
     *     (should be one of the states from VideoPlayerState)
     * @param {number} newState Optional param indicating the state that the
     *     player is transitioning to, if this applies. E.g. if the video was
     *     just paused, then existingState might be VideoPlayerState.PLAYING
     *     and newState might be VideoPlayerState.PAUSED.
     * @param {boolean} calledFromTimer Optional param indicating that the call
     *     was made by the periodic timer, and not triggered by a player state
     *     change. Should not be used by any external callers.
     */
    _handleVideoLogPoll: function (
        this: VideoPlayerModelType,
        existingState: VideoPlayerStateT,
        newState: VideoPlayerStateT,
        calledFromTimer: boolean,
    ) {
        const now = new Date();
        const videoLogState = this.get("_videoLogState");
        const {lastSecondWatched} = this._getUpdatedSecondsWatched(
            existingState,
            newState,
            videoLogState,
            now,
        );
        const videoDuration = this.getDuration();

        // This case *should* never happen, because setPlayerState *should* be
        // called with VideoPlayerState.ENDED immediately when the YouTube
        // player stops (and setPlayerState should, in turn, call this function
        // with newState==VideoPlayerState.ENDED). But empirically we've
        // observed that this doesn't always happen, leading to the
        // autoSaveTimer trigger of this function running indefinitely and
        // sending impossible log_compat data under the impression that the
        // video is still running.  This condition will catch that edge case by
        // noticing when our inferred last_second_watched is impossible (i.e.
        // larger than the duration of the video, including a margin for
        // imprecision), and essentially repurpose this call as the missed
        // video-end call.
        if (
            calledFromTimer === true &&
            existingState === VideoPlayerState.PLAYING &&
            videoDuration !== 0 && // "0" means we're not sure, so we skip!
            lastSecondWatched > videoDuration + 1
        ) {
            this.setPlayerState(VideoPlayerState.ENDED);
            // TODO(riley): Check back on these logs every so often. When we
            //              stop entering this code block, we can simplify the
            //              model.
            GoogleAnalytics.sendNonInteractionEventToGA(
                "event",
                "Diagnostics",
                "video-player-state-reset",
            );
            return;
        }

        if (existingState === VideoPlayerState.PLAYING) {
            // FrozenVideos use readableId but ContentListItems use slug.
            // TODO(kevinb): update player model use id instead.
            const readableId = this.get("readableId") || this.get("slug");
            const action: Action = {
                type: "UPDATE_LAST_SECOND_WATCHED",
                readableId,
                lastSecondWatched,
            };

            userInfoStore.dispatch(action);
        }

        this.set("numPolls", this.get("numPolls") + 1);

        const playbackJustStarted = newState === VideoPlayerState.PLAYING;
        const playbackJustStopped =
            newState !== undefined && newState !== VideoPlayerState.PLAYING;

        // Set a timer to autosave progress and to check whether the JS is
        // still running (see the long comment below explaining the idea behind
        // polling).
        if (playbackJustStarted) {
            this.set(
                "autoSaveTimer",
                // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
                // eslint-disable-next-line no-restricted-syntax
                setInterval(
                    () =>
                        this._handleVideoLogPoll(
                            this.get("playerState"),
                            undefined,
                            true,
                        ),
                    this.SANITY_CHECK_POLL_INTERVAL,
                ),
            );
        } else if (playbackJustStopped) {
            // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
            // eslint-disable-next-line no-restricted-syntax
            this.set("autoSaveTimer", clearInterval(this.get("autoSaveTimer")));
        }

        // When calculating seconds_watched and points_earned, we use the
        // difference in time between now and the last state change. We use the
        // wall time instead of using a delta between video playback positions,
        // because it's difficult to keep track of video position between seeks
        // (specifically, when a user does a manual seek, we can't tell what
        // time they *were* at before they seeked). This strategy has a
        // problem, though: if, somehow, our client JS stops executing for some
        // period of time, then when we wake up again and go to report to the
        // server, currentTime - lastPollTime might be very large, even though
        // the user wasn't watching the video during that time. So... to
        // address this, we can check every SANITY_CHECK_POLL_INTERVAL ms to
        // make sure we're still awake. If 2*SANITY_CHECK_POLL_INTERVAL ms
        // elapses, and playback didn't just start (i.e. we didn't just now
        // start the autosave/sanity check timer, see above), then we must have
        // fallen asleep at some point somehow, and we should probably ignore
        // that time.
        if (
            !playbackJustStarted &&
            // @ts-expect-error - TS2362 - The left-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type.
            now - videoLogState.lastPollTime >
                2.5 * this.SANITY_CHECK_POLL_INTERVAL
        ) {
            // We're going to ignore the time we missed. If we reset
            // lastPollTime, then that time won't count towards seconds_watched
            // or points_earned, and time going forward will just be picked up
            // by future _handleVideoLogPoll polls/calls.
            this.set(
                "_videoLogState",
                icepick.assoc(videoLogState, "lastPollTime", now),
            );
            // Don't do any further analysis or anything. We're effectively
            // ignoring the time we missed, as if it didn't happen, so there is
            // nothing new for us to do since the last poll.

            // Event-code 'a': executed "falling asleep" codepath
            this._updatePlayerStateHistory("a", existingState, newState);
            return;
            // TODO(ryan): Maybe log a warning?
        }

        // Update videoLogState, and flush state if necessary.
        let newVideoLogState = icepick.assign(
            videoLogState,
            // Updates secondsWatchedSinceSave:
            this._getUpdatedSecondsWatched(
                existingState,
                newState,
                videoLogState,
                now,
            ),

            // *Might* override secondsWatchedSinceSave, percentLastSaved:
            this._flushVideoLogStateAsNecessary(
                existingState,
                newState,
                videoLogState,
                now,
            ),

            {
                lastPollTime: now,
            },
        );

        // Determine the last second watched. We do this last because the
        // fallback values we use are calculated above.
        // Ideally this value will come from the video-player, but in some
        // cases (see getVideoPosition for details) that doesn't work, so we
        // fallback to using our "dead reckoning" guess for lastSecondWatched.
        // Additionally, in some cases getVideoPosition may provide us with an
        // impossible zero value when we know that we've watched some seconds
        // of video (i.e. secondsWatchedSinceSave>0), so we correct the value
        // if necessary.
        const inferredLastSecondWatched = Math.max(
            this.getVideoPosition(newVideoLogState.lastSecondWatched),
            newVideoLogState.secondsWatchedSinceSave,
        );
        newVideoLogState = icepick.assign(newVideoLogState, {
            lastSecondWatched: inferredLastSecondWatched,
        });

        // Handle site-wide VideoAlmostComplete event emission if we've passed
        // the necessary video completion threshold.
        const shouldEmitVideoAlmostCompleteEvent =
            existingState === VideoPlayerState.PLAYING &&
            videoLogState.percentLastSaved <
                this.VIDEO_ALMOST_COMPLETE_PERCENT &&
            this.getCurrentPositionAsPercent(inferredLastSecondWatched) >=
                this.VIDEO_ALMOST_COMPLETE_PERCENT;

        if (shouldEmitVideoAlmostCompleteEvent) {
            emitVideoAlmostComplete();
        }

        this.set("_videoLogState", newVideoLogState);

        // Update this.points
        this._updatePointsEstimate(newVideoLogState.secondsWatchedSinceSave);
    },

    /**
     * Calls _flushVideoLogState to save progress to the server if one of the
     * following conditions are met:
     *   a) the video just started (at the very beginning)
     *   b) the video just stopped playback, and >1s has elapsed since the last
     *      save
     *   c) the video just ended (reached the very end)
     *   d) we crossed some percentage or time threshold during playback and
     *      need to autosave
     *   e) we crossed the percentage boundary required for a user to earn full
     *      energy points
     */
    _flushVideoLogStateAsNecessary: function (
        this: VideoPlayerModelType,
        existingPlayerState: VideoPlayerStateT,
        newPlayerState: VideoPlayerStateT,
        videoLogState: VideoLogState,
        now: Date,
    ) {
        const playbackJustStarted = newPlayerState === VideoPlayerState.PLAYING;
        const playbackJustStopped =
            newPlayerState !== undefined &&
            newPlayerState !== VideoPlayerState.PLAYING;
        const {secondsWatchedSinceSave, lastSecondWatched} =
            this._getUpdatedSecondsWatched(
                existingPlayerState,
                newPlayerState,
                videoLogState,
                now,
            );

        const videoStarted =
            playbackJustStarted &&
            this.getVideoPosition(videoLogState.lastSecondWatched) < 1;
        const videoPaused =
            existingPlayerState === VideoPlayerState.PLAYING &&
            playbackJustStopped === true &&
            secondsWatchedSinceSave > 1;
        const videoEnded =
            newPlayerState === VideoPlayerState.ENDED &&
            secondsWatchedSinceSave >= 1;
        const shouldAutosave = this._isAutoSaveIntervalExceeded(
            secondsWatchedSinceSave,
        );
        const gotFullPoints =
            existingPlayerState === VideoPlayerState.PLAYING &&
            videoLogState.percentLastSaved <
                this.REQUIRED_PERCENT_FOR_FULL_POINTS &&
            // TODO(csilvers): this should probably be percent watched.
            this.getCurrentPositionAsPercent(lastSecondWatched) >=
                this.REQUIRED_PERCENT_FOR_FULL_POINTS;

        if (
            videoStarted ||
            videoPaused ||
            videoEnded ||
            shouldAutosave ||
            gotFullPoints
        ) {
            const gaReferrer = GoogleAnalytics.getGAReferrer();
            this._flushVideoLogState(
                secondsWatchedSinceSave,
                lastSecondWatched,
                existingPlayerState,
                newPlayerState,
                gaReferrer,
            );

            return {
                // TODO(ryan): Setting secondsWatchedSinceSave = 0 here isn't
                // really optimal, because we don't yet know if the save
                // completed successfully. If the save fails, it would be nice
                // not to reset secondsWatchedSinceSave, so that the time can
                // count towards the next save. However, the problem is we can't
                // actually tell if a save really fails, or if the
                // log_compatability API request just timed out or failed in
                // some other way.
                secondsWatchedSinceSave: 0,
                percentLastSaved:
                    this.getCurrentPositionAsPercent(lastSecondWatched),
            };
        }
        // Event-code 'b': no need to flush.
        this._updatePlayerStateHistory(
            "b",
            existingPlayerState,
            newPlayerState,
            secondsWatchedSinceSave,
            lastSecondWatched,
        );
        return {};
    },

    _flushVideoLogState: function (
        this: VideoPlayerModelType,
        secondsWatchedSinceSave: number,
        lastSecondWatched: number,
        existingPlayerState: VideoPlayerStateT,
        newPlayerState: VideoPlayerStateT,
        gaReferrer: string,
    ) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        if (this.get("savingVideoLogState")) {
            // Event-code 'c': attempted to save while already saving.
            this._updatePlayerStateHistory(
                "c",
                existingPlayerState,
                newPlayerState,
                secondsWatchedSinceSave,
                lastSecondWatched,
            );
            return;
        }
        this.set("savingVideoLogState", true);

        // Make sure cookies are enabled, otherwise this totally won't work.
        if (!Cookies.areCookiesEnabled()) {
            Log.log("Cookies seem to be disabled. Not logging progress.");
            // Event-code 'd': cookies are disabled.
            this._updatePlayerStateHistory(
                "d",
                existingPlayerState,
                newPlayerState,
                secondsWatchedSinceSave,
                lastSecondWatched,
            );
            return;
        }

        // During the 60 Minutes crunch, things started backing up and failing,
        // so komalo added this to relieve some of the pressure on the queues.
        if (this.consecutiveFailures >= this.get("maxConsecutiveFailures")) {
            Log.error(
                "Not sending video log due to too many failures",
                Errors.Internal,
            );
            // Event-code 'e': too many failures
            this._updatePlayerStateHistory(
                "e",
                existingPlayerState,
                newPlayerState,
                secondsWatchedSinceSave,
                lastSecondWatched,
            );
            return;
        }

        // No event-code: we're going to log *right now*!
        this._updatePlayerStateHistory(
            "",
            existingPlayerState,
            newPlayerState,
            secondsWatchedSinceSave,
            lastSecondWatched,
        );

        // Clear the player-state history now that we're logging it.
        this.set("playerStateHistory", "");

        // Some of these values (eg. "user_mission_id") may be
        // undefined. We filter out all undefined values because sending them
        // up with the request may cause errors.
        const mutationParams = stripUndefined({
            // Required
            contentId: this.get("contentId"),
            secondsWatched: secondsWatchedSinceSave || 0,
            lastSecondWatched: lastSecondWatched || 0,

            // Optional
            durationSeconds: this.getDuration(),
            // This appears to be undefined for at least some videos embedded in
            // articles.
            lessonId: this.get("lessonID"),
            captionsLocale: this.captionsCurrentLanguage(),
            fallbackPlayer: this.get("playerType") === "html",
            localTimezoneOffsetSeconds:
                -1 * new Date().getTimezoneOffset() * 60,
            gaReferrer: gaReferrer,
        });

        if (this.videoTaskView) {
            this.videoTaskView.model.recordWatch(
                lastSecondWatched,
                secondsWatchedSinceSave,
            );
        }

        // NOTE(jvoll): Log activity for Learning Time v2 calculations. DO NOT
        // fire a conversion if the player state is being update to PAUSED. This
        // often means that the user has closed the video modal. By the time
        // this conversion fires the modal is likely already closed and we've
        // fired another conversion indicating the new activity that the user is
        // doing. Firing this conversion at that point will result in counting
        // additional video minutes when in reality the user is now doing
        // something else.
        //
        // The only case where this could cause us to miss counting time is if the
        // user hits the pause button and walks away from their computer (without
        // closing the tab). In this case we will count up until the last
        // `video_log_client` or `CONTINUATION` event, but that is never that
        // long ago, especially for logged in users (see _isAutoSaveIntervalExceeded).
        if (newPlayerState !== VideoPlayerState.PAUSED) {
            this.markConversion({
                id: "video_log_client",
                extra: {
                    ...mutationParams,
                    youtubeId: this.get("youtubeId"),
                },
            });
        }

        // According to TypeScript this is impossible (i.e. initialize-player-model
        // will always send us `contentId`). However, the TypeScript types in
        // this area of the code are not perfect and if something changes in
        // the way we initialize this backbone model such that this ends up
        // unset that would definitely be a problem.
        if (!mutationParams.contentId) {
            // If we're a Lite Video then we don't have a content ID, so it's
            // reasonable for us to bail here, we don't need update the user's
            // video progress.
            if (this.get("liteVideo")) {
                return;
            }

            const metadata = {
                sentry: {
                    extras: {
                        youtubeId: this.get("youtubeId"),
                    },
                },
            } as const;
            Log.error(
                "ContentId not set in call to updateUserVideoProgress",
                Errors.Internal,
                {metadata},
            );
        }

        // If we change to the completed state, we want to refresh some data.
        const refetchQueries = (
            response: ApolloResult<UpdateUserVideoProgressData>,
        ) => {
            const data = response.data?.updateUserVideoProgress;
            const currentCompletionStatus = this.get("completed");

            if (data && !data.error && data.videoItemProgress) {
                const newCompletionStatus = data.videoItemProgress.completed;

                if (!currentCompletionStatus && newCompletionStatus) {
                    // refetch the UserAssignments query so that the user
                    // sees the changes in the LIHP
                    return ["UserAssignments"];
                }
            }

            return [];
        };

        // This is one of our most expensive routes in terms of instance hours,
        // therefore we use the multithreaded module to try to keep costs down.
        // TODO(somewhatabstract, JIRA-XXX3-20210907): Improve our patterns so that we
        // can pass a valid ApolloGqlClient from the react components.
        // eslint-disable-next-line no-restricted-syntax
        const gqlClient = getUnsafeApolloGqlClient();
        gqlClient
            .mutate(UPDATE_USER_VIDEO_PROGRESS_MUTATION, {
                variables: {input: mutationParams},
                module: "multithreaded",
                refetchQueries,
            })
            .then((response) => {
                const data = response.data?.updateUserVideoProgress;

                // TODO(dan-lampman): Verify actionResults against the existing
                // schemas instead of trusting that the following code handles
                // errors for malformed data.
                if (data?.error) {
                    const errorCode = data.error.code;
                    // If multiple videos are played at the same time, the
                    // mutation will sometimes return an error due to the
                    // detect_cheat logic in video_log.py. No sense in
                    // logging an error for that.
                    if (errorCode !== "CHEATING") {
                        self._logFailedProgressUpdate(
                            errorCode,
                            self.get("youtubeId"),
                            lastSecondWatched,
                        );
                    }
                    self.consecutiveFailures += 1;
                } else if (data?.videoItemProgress) {
                    try {
                        self.consecutiveFailures = 0;
                        APIActionResults.respondToGraphQLAction(
                            data.actionResults,
                        );
                        self._finishSave(data.videoItemProgress);
                    } catch (err: any) {
                        // We want to catch all other errors here such as as missing or
                        // malformed fields on the response object.
                        // If we're not provided a status code then we assume 500.
                        const errorCode = err.code || 500;
                        self._logFailedProgressUpdate(
                            errorCode,
                            self.get("youtubeId"),
                            lastSecondWatched,
                            err,
                        );
                    }
                } else {
                    // This _should_ not happen, if something went wrong
                    // `data.error` better not be null.
                    Log.error(
                        "updateUserVideoProgress came back with both a null error and null videoItemProgress.",
                        Errors.Internal,
                    );
                }
            })
            .catch((err) => {
                Log.error(
                    "Failed to update user video progress",
                    Errors.Internal,
                    {cause: err},
                );
                self.consecutiveFailures += 1;
            })
            .then(() => {
                // Always reset the "saving" state.
                self.set("savingVideoLogState", false);
            });
    },

    _logFailedProgressUpdate: function (
        errorCode: string,
        youtubeId: string,
        lastSecondWatched: number,
        sourceError = null,
    ) {
        Log.error(
            "Failed to update user video progress. Error returned from GraphQL mutation",
            Errors.Internal,
            {
                cause: sourceError,
                sentryData: {
                    fingerprint: ["updateUserVideoProgress", errorCode],
                    contexts: {
                        extras: {
                            errorCode,
                            youtubeId,
                            lastSecondWatched: lastSecondWatched,
                            youtubeLanguage: getYoutubeLanguage(),
                        },
                    },
                },
            },
        );
    },

    captionsCurrentLanguage: function (this: VideoPlayerModelType) {
        // If it was even enabled.
        let enabled = false;
        let lang = "";
        let track: Record<string, any> = {};

        if (!this.get("useCaptionsDetectionFallback")) {
            // Use the event based checker if an event's fired,
            // it's more accurate.
            return this.get("captionsLocale");
        }
        if (this.player == null) {
            Log.log("No player, no captions");
            return lang;
        }
        // Either "cc" or "captions" is the correct key
        // depending on if it's the html5 or other youtube player.
        // Otherwise we get errors.
        try {
            enabled = enabled || this.player.getOptions("cc").length > 0;
            track = this.player.getOption("cc", "track");
        } catch (err: any) {} // eslint-disable-line no-empty

        if (!enabled) {
            try {
                enabled = this.player.getOptions("captions").length > 0;
                track = this.player.getOption("captions", "track");
            } catch (err: any) {} // eslint-disable-line no-empty
        }

        if (enabled) {
            if ("languageCode" in track) {
                lang = track["languageCode"];
            }
        }

        return lang;
    },

    _finishSave: function (
        this: VideoPlayerModelType,
        data: VideoItemProgress,
    ) {
        this.set("pointsSaved", data.points);

        const {points, ...rest} = data;

        // This is to keep the displayed score from jumping backwards if the
        // estimator has already updated it before the server responds.
        if (points < this.get("points")) {
            // `points` omitted
            this.set(rest);
        } else {
            this.set(data);
        }

        // The HtmlPlayerWrapper requires downloadUrls to be parsed and set
        // directly on the model (not nested under content). This is important
        // since this is what allows the fallback player to fetch the video.
        try {
            this.set("downloadUrls", JSON.parse(data.content.downloadUrls));
        } catch (err: any) {
            Log.error("Failed to parse downloadUrls", Errors.Internal, {
                cause: err,
            });
        }

        const action: Action = {
            type: "UPDATE_STATUS",
            status: data.completed ? "complete" : "started",
            progressKey: data.content.progressKey,
        };

        if (data.completed) {
            this.get("onComplete")?.();
        }

        userInfoStore.dispatch(action);
    },

    /**
     * @return {number} The current seek time in the video the player is at.
     *     If the video player is not set up yet, return 0.  If the
     *     video_player seems to always return 0, *and* the user has never
     *     seeked in this video, return fallbackValue, which is our best
     *     guess as to the current position using "dead reckoning".
     *     Otherwise, just return 0.
     *     TODO(csilvers): instead, in this last case, return undefined
     *                     and propagate that knowledge up to the user.
     *
     *     Here are some browser extensions that break getCurrentTime():
     *        http://stackoverflow.com/questions/35781618/player-getcurrenttime-returns-zero
     *        https://productforums.google.com/forum/#!profile/chrome/APn2wQfYfkoPWARsRQPvZl5dEZWtoFzwL4TrIoQAzt3N1n7F_E12oKQkMTfrEwNa4dHEmnoAEDuA/youtube/F5CEukwhitA/w699OBvn7fgJ
     *
     *     Callers beware: we've observed that in some cases, for some users,
     *     the YouTube API behaves unreliably, causing this function to return
     *     0 when it shouldn't. (This may be due to the browser extensions
     *     listed above!) Consider reasonability-checking the return value
     *     against other data (such as seconds_watched, which may indicate a
     *     larger actual value)
     */
    getVideoPosition: function (
        this: VideoPlayerModelType,
        fallbackValue: number,
        calledFromRender: boolean,
    ) {
        if (!this.player || !this.player.getCurrentTime) {
            return 0;
        }
        // Setting forces an update, so we don't want to do this from `render`.
        if (this.player.getCurrentTime() > 0 && !calledFromRender) {
            this.set("getCurrentTimeIsDefinitelyWorking", true);
        }
        if (this.get("getCurrentTimeIsDefinitelyWorking")) {
            return this.player.getCurrentTime() || 0;
        }
        if (this.get("userHasHitPauseOnThisVideo")) {
            // Once the user has 'scrubbed' -- moved the location
            // button somewhere else to skip around in the video --
            // our fallbackValue is unreliable since it relies on
            // 'dead reckoning'.  Unfortunately it's hard to tell if a
            // user has ever scrubbed.  However, a scrub always starts
            // with a 'pause' and ends with a 'play', so to be safe
            // we'll just assume there's been scrubbing whenever
            // there's been pausing.
            return 0; // better would be to return undefined
        }
        return fallbackValue || 0;
    },

    _updatePointsEstimate: function (
        this: VideoPlayerModelType,
        secondsSinceSave: number,
    ) {
        const duration = this.getDuration();
        if (duration === 0) {
            return;
        }

        const percentSinceSave = Math.min(1.0, secondsSinceSave / duration);
        let percentTotal =
            percentSinceSave +
            this.get("pointsSaved") / this.get("possiblePoints");
        if (percentTotal > this.REQUIRED_PERCENT_FOR_FULL_POINTS) {
            percentTotal = 1.0;
        }

        const estimate = Math.floor(this.get("possiblePoints") * percentTotal);

        if (estimate < this.get("points")) {
            // If we ever estimate fewer points than are currently being shown,
            // don't update the points estimate. This can happen while a save's
            // ajax request is outbound, because pointsSaved won't be updated
            // until the save data returns.
            return;
        }

        this.set({points: estimate});
    },

    /**
     * We only want to save if either a certain percentage of the video has
     * been watched or if a certain number of seconds has passed.
     */
    _isAutoSaveIntervalExceeded: function (
        this: VideoPlayerModelType,
        secondsWatchedSinceSave: number,
    ) {
        const secsPercent =
            this.getDuration() * this.get("percentBetweenSaves");
        const interval = Math.min(secsPercent, this.get("secondsBetweenSaves"));
        return secondsWatchedSinceSave >= interval;
    },

    setPlaybackRate: function (this: VideoPlayerModelType, rate: number) {
        // Update viewing state so that the video duration viewed is calculated
        // properly.
        this._handleVideoLogPoll(this.get("playerState"));
        this.set("playbackRate", rate);
    },

    // fallbackPosition is the best-guess position, as per getVideoPosition().
    getCurrentPositionAsPercent: function (
        this: VideoPlayerModelType,
        fallbackPosition: number,
    ) {
        const duration = this.getDuration();
        if (duration === 0) {
            return 0;
        }

        return Math.min(
            1.0,
            this.getVideoPosition(fallbackPosition) / duration,
        );
    },

    getDuration: function (this: VideoPlayerModelType) {
        const duration =
            (this.player &&
                this.player.getDuration &&
                this.player.getDuration()) ||
            this.get("duration") ||
            0;
        return Math.max(0, Math.floor(duration));
    },

    /**
     * Sets the state of the player as of when the player was instantiated.
     *
     * This would normally by UNSTARTED, but it's possible for a user to start
     * playing a YouTube video before any Khan Academy video-related JavaScript
     * has executed. In particular, this happens when the YouTube iframe player
     * is server-side rendered into the page.
     *
     * We also make sure that the initial playback rate of the player and this
     * model are synchronized.
     */
    setInitialPlayerState: function (
        this: VideoPlayerModelType,
        state: VideoPlayerStateT,
        playbackRate: number,
    ) {
        if (playbackRate != null) {
            this.setPlaybackRate(playbackRate);
        }

        if (state === VideoPlayerState.PLAYING) {
            // If the initial state of the player is "playing", it means the
            // video was started by the user clicking the play button on
            // a server-side rendered YouTube iframe. In this case, to
            // accurately track progress, we estimate how long ago they started
            // playing the video.
            //
            // For now, we blindly assume they started playing the video from
            // the beginning and didn't seek.
            //
            // We set the time watched since the last save to be this amount of
            // time (there was no last save), and initialize lastPollTime so
            // that we start measuring the amount of time watched going
            // forward. (See _handleVideoLogPoll for more details.)
            const currentVideoPosition = this.getVideoPosition(0);
            this.set(
                "_videoLogState",
                icepick.assign(
                    this.get("_videoLogState"),
                    // Set initial secondsWatchedSinceSave and lastSecondWatched
                    {
                        secondsWatchedSinceSave: currentVideoPosition,
                        lastSecondWatched: currentVideoPosition,
                    },
                ),
            );
        }

        this.setPlayerState(state);
    },

    /**
     * Set the player state.
     *
     * This should be triggered by event handlers on the video player whenever
     * the state of the player changes (if the video starts, stops, buffers,
     * etc... Changing the playback speed is not considered a state change).
     *
     * @param {number} state The newly set state. Should be one of the fields
     *      of VideoPlayerState.
     */
    setPlayerState: function (
        this: VideoPlayerModelType,
        state: VideoPlayerStateT,
    ) {
        // This function is mostly responsible for firing conversions when the
        // player state changes. We delegate stuff related to video logging
        // (which also includes updating seconds_watched and updating points
        // earned) to _handleVideoLogPoll. Note that _handleVideoLogPoll
        // references both `this.get("playerState")` as the old state and
        // `state` as the new state, so this line must come before the
        // `this.set({playerState: state})` line below.
        this._handleVideoLogPoll(this.get("playerState"), state);

        const oldState = this.get("playerState");
        this.set({playerState: state});

        if (state === VideoPlayerState.UNSTARTED) {
            this.set({userHasHitPauseOnThisVideo: false});
        } else if (state === VideoPlayerState.PAUSED) {
            this.set({userHasHitPauseOnThisVideo: true});
        }

        // If video playback just started/resumed, log the relevant bigbingo
        // conversions.
        if (
            oldState !== VideoPlayerState.PLAYING &&
            state === VideoPlayerState.PLAYING
        ) {
            // If we have not played the video since the page was loaded,
            // trigger the BigBingo video_start_in_pageview conversion
            if (!this.get("hasPlayed")) {
                this.markConversion({
                    id: "video_started_in_pageview",
                    extra: {
                        content_id: this.attributes.id,
                        slug: this.attributes.nodeSlug,
                        fallbackPlayer: this.get("playerType") === "html",
                    },
                });
                this.set("hasPlayed", true);
                this.get("onStart")?.();
            }
        }
    },
});

export default VideoPlayerModel;
